import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, Calendar, User, Tag, ArrowRight } from "lucide-react";
import SEO, { breadcrumb } from "../components/SEO";

// Surcharges SEO ciblees (balise <title> + meta description) sans modifier le H1
// (post.title) ni l'extrait affiche (post.excerpt). Cle = slug de l'article.
const SEO_OVERRIDES = {
  "obtenir-activer-e-cps": {
    title: "e-CPS : l'obtenir et l'activer sans blocage (2026)",
    exact: true,
    description: "e-CPS : prérequis RPPS, activation par l'appli mobile et usage avec Pro Santé Connect, pas à pas. Le guide complet pour l'obtenir sans blocage en 2026.",
  },
  "gestion-crise-cyber-sante-que-faire": {
    title: "Cyberattaque en santé : que faire ? Le plan en 6 étapes",
    description: "Cyberattaque dans un établissement ou un cabinet de santé : isoler, alerter le CERT Santé, notifier, restaurer. Le plan d'action en 6 étapes, sans perdre le contrôle.",
  },
};
import NotFound from "./NotFound";
import { API } from "../apiBase";
import { extractFaqFromMarkdown, buildFaqSchema } from "../utils/faqExtractor";

const SITE = "https://metaconnect.fr";
const REMARK_PLUGINS = [remarkGfm];

function wordCount(markdown) {
  if (!markdown) return 0;
  const plain = markdown
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/[#*_>`-]/g, " ")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1");
  return plain.split(/\s+/).filter(Boolean).length;
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [redirectTo, setRedirectTo] = useState(null);
  const [noIndex, setNoIndex] = useState(false);

  useEffect(() => {
    setRedirectTo(null); setNoIndex(false); setNotFound(false); setPost(null); setLoading(true);
    (async () => {
      try {
        const rules = await axios.get(`${API}/blog/seo-rules`, { timeout: 5000 }).then((r) => r.data).catch(() => null);
        const target = rules?.redirects?.[slug];
        if (target) { setRedirectTo(target); return; }
        setNoIndex(Boolean(rules?.noindex?.includes(slug)));
        const { data } = await axios.get(`${API}/blog/posts/${slug}`);
        setPost(data);
      } catch (e) {
        if (e?.response?.status === 404) setNotFound(true);
      } finally { setLoading(false); }
    })();
  }, [slug]);

  const postSchema = useMemo(() => {
    if (!post) return null;
    const coverUrl = post.cover_image
      ? (post.cover_image.startsWith("http") ? post.cover_image : `${SITE}${post.cover_image}`)
      : `${SITE}/og-image.jpg`;
    const wc = wordCount(post.content);
    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "@id": `${SITE}/blog/${post.slug}#article`,
      headline: post.title,
      alternativeHeadline: post.excerpt || post.title,
      description: post.excerpt || "",
      image: {
        "@type": "ImageObject",
        url: coverUrl,
        width: 1200,
        height: 630,
      },
      datePublished: post.published_at,
      dateModified: post.updated_at || post.published_at,
      author: {
        "@type": "Organization",
        name: post.author || "MetaConnect",
        url: SITE,
      },
      publisher: {
        "@type": "Organization",
        name: "MetaConnect",
        url: SITE,
        logo: {
          "@type": "ImageObject",
          url: `${SITE}/icon-512.png`,
          width: 512,
          height: 512,
        },
      },
      mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE}/blog/${post.slug}` },
      url: `${SITE}/blog/${post.slug}`,
      keywords: (post.tags || []).join(", "),
      articleSection: (post.tags && post.tags[0]) || "Numérique en santé",
      wordCount: wc,
      inLanguage: "fr-FR",
      isAccessibleForFree: true,
      isPartOf: { "@type": "Blog", "@id": `${SITE}/blog`, name: "Blog MetaConnect" },
    };
    const schemas = [
      articleSchema,
      breadcrumb([
        { name: "Accueil", url: "/" },
        { name: "Blog", url: "/blog" },
        { name: post.title, url: `/blog/${post.slug}` },
      ]),
    ];
    // Auto-extract FAQ Schema from markdown (Rich Snippets boost)
    const faqs = extractFaqFromMarkdown(post.content || "");
    if (faqs.length >= 2) {
      schemas.push(buildFaqSchema(faqs));
    }
    return schemas;
  }, [post]);

  if (redirectTo && redirectTo !== `/blog/${slug}`) return <Navigate to={redirectTo} replace />;
  if (notFound) return <NotFound />;
  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--mc-navy-950)] hex-grid grid place-items-center">
        <div className="text-[#475569] text-sm">Chargement…</div>
      </div>
    );
  }
  if (!post) return null;

  return (
    <div className="bg-[var(--mc-navy-950)]" data-testid="page-blog-post">
      <SEO
        noIndex={noIndex}
        title={SEO_OVERRIDES[post.slug]?.title || post.title}
        exactTitle={Boolean(SEO_OVERRIDES[post.slug]?.exact)}
        description={SEO_OVERRIDES[post.slug]?.description || post.excerpt || `${post.title} — MetaConnect Blog`}
        canonical={`/blog/${post.slug}`}
        type="article"
        image={post.cover_image || "/og-image.jpg"}
        schema={postSchema}
      />

      <article className="hex-grid pt-32 pb-16 lg:pt-40">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <Link to="/blog" className="inline-flex items-center gap-1.5 text-[12px] text-[#475569] hover:text-[#0F2B5B] mb-6 transition">
            <ArrowLeft size={14} /> Tous les articles
          </Link>

          <div className="flex flex-wrap gap-2 mb-5">
            {(post.tags || []).map((t) => (
              <span key={t} className="text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 rounded-full border border-[rgba(127,179,255,0.3)] bg-[rgba(127,179,255,0.05)] text-[#0F2B5B] inline-flex items-center gap-1">
                <Tag size={10} /> {t}
              </span>
            ))}
          </div>

          <h1 className="font-display text-[#0F2B5B] text-4xl sm:text-5xl font-semibold leading-[1.1]">{post.title}</h1>
          {post.excerpt && (
            <p className="mt-5 text-[17px] text-[#475569] leading-relaxed">{post.excerpt}</p>
          )}

          <div className="mt-7 flex items-center gap-4 text-[12px] text-[#475569]">
            <span className="inline-flex items-center gap-1.5"><User size={12} />{post.author || "MetaConnect"}</span>
            <span className="inline-flex items-center gap-1.5"><Calendar size={12} />
              {post.published_at && new Date(post.published_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })}
            </span>
          </div>

          {post.cover_image && (
            <div className="mt-10 rounded-2xl overflow-hidden border border-slate-200 bg-[#F1F5F9]">
              <img
                src={post.cover_image}
                alt={post.title}
                loading="eager"
                className="w-full h-auto max-h-[600px] object-contain mx-auto"
              />
            </div>
          )}
        </div>

        <div className="max-w-3xl mx-auto px-5 lg:px-8 mt-12">
          <div className="prose-mc">
            <ReactMarkdown remarkPlugins={REMARK_PLUGINS}>{post.content || ""}</ReactMarkdown>
          </div>
        </div>
      </article>

      <section className="bg-[var(--mc-navy-900)] py-16 mt-12">
        <div className="max-w-3xl mx-auto px-5 lg:px-8 text-center">
          <h2 className="font-display text-[#0F2B5B] text-2xl">Envie d'aller plus loin ?</h2>
          <p className="mt-3 text-[#475569]">Découvrez MetaConnect en démo personnalisée (30 min).</p>
          <Link to="/demo" className="mt-6 inline-flex btn-primary px-6 py-3 rounded-full text-[13px] font-medium gap-2 items-center">
            Planifier une démo <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
}
